import type { TenantSettingsPatch } from '@/entities/tenant';

import { AutomationCard } from '../components/AutomationCard';
import { CostSourcesCard } from '../components/CostSourcesCard';
import { NhgSettingsCard } from '../components/NhgSettingsCard';
import { ReportSettingsCard } from '../components/ReportSettingsCard';
import { reportCopy } from '../copy';
import {
    usePdfBacklog,
    useTemplateCostSources,
    useTenantSettings,
    useUpdateSettings,
} from '../hooks';
import { SettingsSaveError, SettingsSection } from './TabLayout';
import { useFocusOnHash } from './useFocusOnHash';

/**
 * Tabblad Rapport: wat een nieuw rapport en zijn link meekrijgen, en wat er
 * vanzelf gebeurt.
 *
 * Op een breed scherm links wat het rapport meekrijgt - de rapportinstellingen
 * en de NHG-bijlage - en rechts de automatisering: twee lijsten schakelaars die
 * naast elkaar lezen. Daaronder, over de hele breedte en standaard ingeklapt, de
 * achtergrond bij de bedragen in de vragenlijsten (alleen voor wie instellingen
 * beheert): cijfers per vragenlijst. Smaller staat alles onder elkaar, in de
 * volgorde van de code.
 *
 * "Ingeschreven als" bij de NHG-bijlage is een plek waar Bedrijfsgegevens naar
 * linkt: komt die link hier uit, dan krijgt dat veld de focus.
 */
export function ReportTab({ canManage }: { canManage: boolean }) {
    const settings = useTenantSettings();
    const pdfBacklog = usePdfBacklog();
    const costSources = useTemplateCostSources(canManage);
    const updateSettings = useUpdateSettings();
    const change = (patch: TenantSettingsPatch) => updateSettings.mutate(patch);

    useFocusOnHash(settings.data !== undefined);

    return (
        <div className="flex max-w-[1120px] flex-col gap-[14px]">
            <SettingsSection
                title={reportCopy.title}
                query={settings}
                onRetry={() => void settings.refetch()}
            >
                {settings.data ? (
                    <>
                        <div className="grid items-start gap-[14px] @min-[860px]:grid-cols-2">
                            <ReportSettingsCard
                                settings={settings.data}
                                canManage={canManage}
                                disabled={updateSettings.isPending}
                                pdf={pdfBacklog.data ?? null}
                                onChange={change}
                            />
                            {/* Rechts, over de hoogte van de twee kaarten links. */}
                            <div className="@min-[860px]:row-span-2">
                                <AutomationCard
                                    settings={settings.data}
                                    canManage={canManage}
                                    disabled={updateSettings.isPending}
                                    onChange={change}
                                />
                            </div>
                            <NhgSettingsCard
                                settings={settings.data}
                                canManage={canManage}
                                disabled={updateSettings.isPending}
                                onChange={change}
                            />
                        </div>
                        <SettingsSaveError error={updateSettings.error} />
                    </>
                ) : null}
            </SettingsSection>

            {canManage ? (
                <CostSourcesCard
                    sources={costSources.data}
                    loading={costSources.isPending}
                    error={costSources.isError ? costSources.error : null}
                    onRetry={() => void costSources.refetch()}
                />
            ) : null}
        </div>
    );
}
